/**
 * Greenway Biotech Hydroponics Calculator
 * Universal calculator for 4-part hydroponic nutrient system
 * Products: Cal-Mag Plus, Grow Green, Micro Green, Blossom Green
 * Version: 1.0.0
 * 
 * Usage: Place <div class="gwb-calculator" data-calc="hydroponics"></div> in HTML
 * The calculator will auto-detect product context or default to Micro Green
 */

window.renderHydroponicsCalculator = function(container) {
  
  // Number formatter for consistent display
  const nf = (v) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(v || 0);

  // Auto-detect product type from page URL or default to micro-green
  let productType = 'micro-green';
  const url = window.location.href.toLowerCase();
  
  if (url.includes('cal-mag') || url.includes('calmag')) {
    productType = 'cal-mag';
  } else if (url.includes('grow-green') || url.includes('growgreen')) {
    productType = 'grow-green';
  } else if (url.includes('blossom-green') || url.includes('blossomgreen')) {
    productType = 'blossom-green';
  } else if (url.includes('micro-green') || url.includes('microgreen')) {
    productType = 'micro-green';
  }
  
  // Allow override via data attribute
  if (container.dataset.product) {
    productType = container.dataset.product;
  }
  
  // Product-specific configuration
  const products = {
    'cal-mag': {
      name: 'Cal-Mag Plus 2-0-0',
      ratio: 2.5, // lbs per gallon for stock solution
      packages: [
        { size: 2.5, label: '2.5 lb package' },
        { size: 5, label: '5 lb package' },
        { size: 10, label: '10 lb package' },
        { size: 25, label: '25 lb package' }
      ],
      stages: {
        seedling: 2.5,
        earlyVeg: 5,
        lateVeg: 7.5,
        transition: 10,
        earlyFlower: 10,
        peakFlower: 10,
        lateFlower: 5
      },
      tips: {
        stock: 'Always add Cal-Mag FIRST to water before any other nutrients',
        hydro: 'Essential for RO water and coco coir - never skip',
        soil: 'Prevents blossom-end rot and tip burn in containers',
        system: 'Foundation of the 4-part system - add first, adjust pH last'
      }
    },
    'grow-green': {
      name: 'Grow Green 4-2-6',
      ratio: 1.7,
      packages: [
        { size: 1.7, label: '1.7 lb package' },
        { size: 3.4, label: '3.4 lb package' },
        { size: 8.5, label: '8.5 lb package' },
        { size: 17, label: '17 lb package' }
      ],
      stages: {
        seedling: 1.25,
        earlyVeg: 5,
        lateVeg: 15,
        transition: 10,
        earlyFlower: 5,
        peakFlower: 0,
        lateFlower: 0
      },
      tips: {
        stock: 'Higher potassium than typical 2-1-6 formulas for stronger stems',
        hydro: 'Taper down gradually when transitioning to flowering',
        soil: 'Use early veg rate (5 ml/gal) for soil drench applications',
        system: 'Primary nitrogen source - reduce to zero by peak bloom'
      }
    },
    'micro-green': {
      name: 'Micro Green 2-0-3',
      ratio: 1.5,
      packages: [
        { size: 1.5, label: '1.5 lb package' },
        { size: 3, label: '3 lb package' },
        { size: 7.5, label: '7.5 lb package' },
        { size: 15, label: '15 lb package' }
      ],
      stages: {
        seedling: 1.25,
        earlyVeg: 5,
        lateVeg: 10,
        transition: 10,
        earlyFlower: 10,
        peakFlower: 10,
        lateFlower: 7.5
      },
      tips: {
        stock: 'Contains chelated minerals - store in dark container (light sensitive)',
        hydro: 'Essential throughout ALL growth stages - never skip micronutrients',
        soil: 'Can be used as foliar spray (5 ml/gal) for quick correction',
        system: 'Complete trace package - maintains constant rate through all stages'
      }
    },
    'blossom-green': {
      name: 'Blossom Green 2-6-8',
      ratio: 1.8,
      packages: [
        { size: 1.8, label: '1.8 lb package' },
        { size: 9, label: '9 lb package' },
        { size: 18, label: '18 lb package' },
        { size: 36, label: '36 lb package' }
      ],
      stages: {
        seedling: 1.25,
        earlyVeg: 5,
        lateVeg: 5,
        transition: 10,
        earlyFlower: 15,
        peakFlower: 20,
        lateFlower: 15
      },
      tips: {
        stock: 'High P-K formula specifically calibrated for flowering phase',
        hydro: 'Start increasing at transition, peak at 20 ml/gal during heavy bloom',
        soil: 'Reduces rates for soil - use 75% of hydroponic recommendations',
        system: 'Replace Grow Green during flowering - high phosphorus drives bud formation'
      }
    }
  };

  // Get current product configuration
  const product = products[productType];

  // Build calculator HTML using ONLY global CSS classes
  const html = `
    <div id="calc">
      <h2>${product.name} Calculator</h2>
      <p class="subtitle">Calculate precise amounts for your growing system</p>
      
      <!-- TABS -->
      <div class="calc-tabs" role="tablist" aria-label="Calculator sections">
        <button class="calc-tab active" data-target="stock" role="tab" aria-selected="true" id="tab-stock">
          🧪 Stock Solution
        </button>
        <button class="calc-tab" data-target="hydro" role="tab" aria-selected="false" id="tab-hydro">
          💧 Hydroponic
        </button>
        <button class="calc-tab" data-target="soil" role="tab" aria-selected="false" id="tab-soil">
          🌱 Soil
        </button>
        <button class="calc-tab" data-target="system" role="tab" aria-selected="false" id="tab-system">
          🌟 4-Part System
        </button>
      </div>
      
      <!-- STOCK SOLUTION SECTION -->
      <div id="calc-stock" class="calc-section active" role="tabpanel" aria-labelledby="tab-stock">
        <h3>Stock Solution Mixer</h3>
        <p class="calc-hint">📋 ${product.ratio} lbs of ${product.name} makes 1 gallon of concentrate</p>
        
        <label for="packageSize">Package Size</label>
        <select id="packageSize">
          ${product.packages.map((pkg, i) => 
            `<option value="${pkg.size}" ${i === 1 ? 'selected' : ''}>${pkg.label}</option>`
          ).join('')}
        </select>
        
        <div class="calc-error" id="error-stock"></div>
        
        <div class="calc-buttons">
          <button class="btn btn-primary" data-action="calculate-stock">Calculate</button>
          <button class="btn btn-secondary" data-action="clear-stock">Clear</button>
        </div>
        
        <div id="result-stock" class="calc-result"></div>
      </div>
      
      <!-- HYDROPONIC SECTION -->
      <div id="calc-hydro" class="calc-section" role="tabpanel" aria-labelledby="tab-hydro">
        <h3>Hydroponic Reservoir</h3>
        <p class="calc-hint">📋 Calculate based on your reservoir size and crop stage</p>
        
        <div class="calc-presets">
          <button class="preset-btn" data-preset-res="20">Small (20 gal)</button>
          <button class="preset-btn" data-preset-res="50">Medium (50 gal)</button>
          <button class="preset-btn" data-preset-res="100">Large (100 gal)</button>
        </div>
        
        <label for="reservoirSize">Reservoir Size (gallons)</label>
        <input type="number" id="reservoirSize" min="0" step="1" placeholder="e.g., 50">
        
        <label for="growthStage">Growth Stage</label>
        <select id="growthStage">
          <option value="seedling">Seedling/Clone (${product.stages.seedling} ml/gal)</option>
          <option value="earlyVeg">Early Vegetative (${product.stages.earlyVeg} ml/gal)</option>
          <option value="lateVeg" selected>Late Vegetative (${product.stages.lateVeg} ml/gal)</option>
          <option value="transition">Transition (${product.stages.transition} ml/gal)</option>
          <option value="earlyFlower">Early Bloom (${product.stages.earlyFlower} ml/gal)</option>
          <option value="peakFlower">Peak Bloom (${product.stages.peakFlower} ml/gal)</option>
          <option value="lateFlower">Late Bloom/Flush (${product.stages.lateFlower} ml/gal)</option>
        </select>
        
        <div class="calc-error" id="error-hydro"></div>
        
        <div class="calc-buttons">
          <button class="btn btn-primary" data-action="calculate-hydro">Calculate</button>
          <button class="btn btn-secondary" data-action="clear-hydro">Clear</button>
        </div>
        
        <div id="result-hydro" class="calc-result"></div>
      </div>
      
      <!-- SOIL SECTION -->
      <div id="calc-soil" class="calc-section" role="tabpanel" aria-labelledby="tab-soil">
        <h3>Soil Application</h3>
        <p class="calc-hint">📋 For container plants and garden beds</p>
        
        <div class="calc-row">
          <div>
            <label for="plantCount">Number of Plants</label>
            <input type="number" id="plantCount" min="1" value="1" step="1">
          </div>
          <div>
            <label for="containerSize">Container Size</label>
            <select id="containerSize">
              <option value="1">1-3 gallon pots</option>
              <option value="5" selected>5 gallon pots</option>
              <option value="10">7-10 gallon pots</option>
              <option value="20">15+ gallon/garden bed</option>
            </select>
          </div>
        </div>
        
        <div class="calc-error" id="error-soil"></div>
        
        <div class="calc-buttons">
          <button class="btn btn-primary" data-action="calculate-soil">Calculate</button>
          <button class="btn btn-secondary" data-action="clear-soil">Clear</button>
        </div>
        
        <div id="result-soil" class="calc-result"></div>
      </div>
      
      <!-- 4-PART SYSTEM SECTION -->
      <div id="calc-system" class="calc-section" role="tabpanel" aria-labelledby="tab-system">
        <h3>Complete 4-Part System Recipe</h3>
        <p class="calc-hint">📋 Get the complete nutrient recipe for your reservoir</p>
        
        <div class="calc-presets">
          <button class="preset-btn" data-preset-sysres="20">Small (20 gal)</button>
          <button class="preset-btn" data-preset-sysres="50">Medium (50 gal)</button>
          <button class="preset-btn" data-preset-sysres="100">Large (100 gal)</button>
        </div>
        
        <label for="systemReservoir">Reservoir Size (gallons)</label>
        <input type="number" id="systemReservoir" min="0" step="1" placeholder="e.g., 50">
        
        <label for="systemStage">Growth Stage</label>
        <select id="systemStage">
          <option value="seedling">Seedlings/Clones (0.5-0.8 EC)</option>
          <option value="earlyVeg">Early Vegetative (1.0-1.4 EC)</option>
          <option value="lateVeg" selected>Late Vegetative (1.4-1.8 EC)</option>
          <option value="transition">Transition (1.6-2.0 EC)</option>
          <option value="earlyFlower">Early Bloom (1.8-2.2 EC)</option>
          <option value="peakFlower">Peak Bloom (2.0-2.5 EC)</option>
          <option value="lateFlower">Late Bloom/Flush (1.5-2.0 EC)</option>
        </select>
        
        <div class="calc-error" id="error-system"></div>
        
        <div class="calc-buttons">
          <button class="btn btn-primary" data-action="calculate-system">Calculate</button>
          <button class="btn btn-secondary" data-action="clear-system">Clear</button>
        </div>
        
        <div id="result-system" class="calc-result"></div>
      </div>
      
      <!-- CONVERSION REFERENCE -->
      <div class="quick-ref">
        <h3>Measurement Conversions</h3>
        <div class="ref-grid">
          <div class="ref-item">1 ml = 0.034 fl oz = 0.2 tsp</div>
          <div class="ref-item">5 ml = 0.17 fl oz = 1 tsp</div>
          <div class="ref-item">15 ml = 0.5 fl oz = 1 tbsp</div>
          <div class="ref-item">30 ml = 1 fl oz = 2 tbsp</div>
        </div>
      </div>
    </div>
  `;
  
  // Inject HTML into container
  container.innerHTML = html;
  
  // ============================================================================
  // TAB SWITCHING
  // ============================================================================
  
  const tabs = container.querySelectorAll('.calc-tab');
  const sections = container.querySelectorAll('.calc-section');
  
  tabs.forEach(tab => {
    tab.addEventListener('click', function() {
      const target = this.dataset.target;
      
      // Update tab states
      tabs.forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      this.classList.add('active');
      this.setAttribute('aria-selected', 'true');
      
      // Update section visibility
      sections.forEach(s => s.classList.remove('active'));
      const targetSection = container.querySelector(`#calc-${target}`);
      if (targetSection) targetSection.classList.add('active');
    });
  });
  
  // ============================================================================
  // PRESET BUTTONS
  // ============================================================================
  
  const presetBtns = container.querySelectorAll('.preset-btn');
  presetBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      // Hydro presets
      if (this.dataset.presetRes) {
        const input = container.querySelector('#reservoirSize');
        if (input) input.value = this.dataset.presetRes;
      }
      // System presets
      if (this.dataset.presetSysres) {
        const input = container.querySelector('#systemReservoir');
        if (input) input.value = this.dataset.presetSysres;
      }
    });
  });
  
  // ============================================================================
  // BUTTON EVENT DELEGATION
  // ============================================================================
  
  container.addEventListener('click', function(e) {
    const button = e.target.closest('button');
    if (!button || !button.dataset.action) return;
    
    const action = button.dataset.action;
    
    // Route to appropriate calculator function
    if (action === 'calculate-stock') calculateStock();
    else if (action === 'clear-stock') clearStock();
    else if (action === 'calculate-hydro') calculateHydro();
    else if (action === 'clear-hydro') clearHydro();
    else if (action === 'calculate-soil') calculateSoil();
    else if (action === 'clear-soil') clearSoil();
    else if (action === 'calculate-system') calculateSystem();
    else if (action === 'clear-system') clearSystem();
  });
  
  // ============================================================================
  // ENTER KEY SUPPORT
  // ============================================================================
  
  container.addEventListener('keypress', function(e) {
    if (e.key === 'Enter' || e.keyCode === 13) {
      const input = e.target;
      if (input.tagName !== 'INPUT' && input.tagName !== 'SELECT') return;
      
      const section = input.closest('.calc-section');
      if (!section) return;
      
      const sectionId = section.id;
      e.preventDefault();
      
      // Trigger calculation for active section
      if (sectionId === 'calc-stock') calculateStock();
      else if (sectionId === 'calc-hydro') calculateHydro();
      else if (sectionId === 'calc-soil') calculateSoil();
      else if (sectionId === 'calc-system') calculateSystem();
    }
  });
  
  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  function showError(sectionId, message) {
    const errorDiv = container.querySelector(`#error-${sectionId}`);
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.add('show');
    }
  }
  
  function hideError(sectionId) {
    const errorDiv = container.querySelector(`#error-${sectionId}`);
    if (errorDiv) {
      errorDiv.textContent = '';
      errorDiv.classList.remove('show');
    }
  }
  
  function showResult(sectionId, content) {
    const resultDiv = container.querySelector(`#result-${sectionId}`);
    if (resultDiv) {
      resultDiv.innerHTML = content;
      resultDiv.classList.add('show');
      
      // Scroll to result on mobile
      if (window.innerWidth < 768) {
        setTimeout(() => {
          resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }
  }
  
  function hideResult(sectionId) {
    const resultDiv = container.querySelector(`#result-${sectionId}`);
    if (resultDiv) {
      resultDiv.innerHTML = '';
      resultDiv.classList.remove('show');
    }
  }
  
  // ============================================================================
  // CALCULATOR FUNCTIONS
  // ============================================================================
  
  /**
   * Calculate stock solution
   */
  function calculateStock() {
    hideError('stock');
    hideResult('stock');
    
    const size = parseFloat(container.querySelector('#packageSize').value);
    const waterNeeded = size / product.ratio;
    
    let resultHTML = `
      <h4>Stock Solution Recipe for ${nf(size)} lb Package</h4>
      <div><strong>${product.name} powder:</strong> ${nf(size)} lbs</div>
      <div><strong>Water needed:</strong> ${nf(waterNeeded)} gallon${waterNeeded !== 1 ? 's' : ''}</div>
      <div><strong>Stock solution yield:</strong> ${nf(waterNeeded)} gallon${waterNeeded !== 1 ? 's' : ''}</div>
      <div><strong>Concentration:</strong> ${product.ratio} lbs per gallon</div>
      <div class="calc-tip">
        💡 <strong>Tip:</strong> ${product.tips.stock}
        <ul style="margin:5px 0 0 20px;font-size:.85rem">
          <li>Use warm water (65-75°F) for best dissolution</li>
          <li>Add powder slowly while stirring continuously</li>
          <li>Mix entire package for uniform concentration</li>
          <li>Label container with product name and date</li>
          <li>Store in cool, dark place - good for 6+ months</li>
        </ul>
      </div>
    `;
    
    showResult('stock', resultHTML);
  }
  
  function clearStock() {
    container.querySelector('#packageSize').selectedIndex = 1;
    hideError('stock');
    hideResult('stock');
  }
  
  /**
   * Calculate hydroponic application
   */
  function calculateHydro() {
    hideError('hydro');
    hideResult('hydro');
    
    const reservoir = parseFloat(container.querySelector('#reservoirSize').value);
    const stage = container.querySelector('#growthStage').value;
    
    if (!reservoir || reservoir <= 0) {
      showError('hydro', 'Please enter a valid reservoir size');
      return;
    }
    
    const rate = product.stages[stage];
    const totalMl = reservoir * rate;
    const totalOz = totalMl * 0.033814;
    
    // EC ranges by stage
    const ecRanges = {
      seedling: '0.5-0.8',
      earlyVeg: '1.0-1.4',
      lateVeg: '1.4-1.8',
      transition: '1.6-2.0',
      earlyFlower: '1.8-2.2',
      peakFlower: '2.0-2.5',
      lateFlower: '1.5-2.0'
    };
    
    let resultHTML = `
      <h4>Results for ${nf(reservoir)} Gallon Reservoir</h4>
      <div><strong>${product.name} needed:</strong> ${nf(totalMl)} ml (${nf(totalOz)} oz)</div>
      <div><strong>Application rate:</strong> ${nf(rate)} ml per gallon</div>
      <div><strong>Expected EC:</strong> ${ecRanges[stage]}</div>
      <div class="calc-tip">
        💡 <strong>Tip:</strong> ${product.tips.hydro}
        <ul style="margin:5px 0 0 20px;font-size:.85rem">
          <li>Mix in proper order: Cal-Mag → Micro → Grow → Blossom</li>
          <li>Stir thoroughly between each nutrient addition</li>
          <li>Never mix concentrates directly together</li>
          <li>Adjust pH to 5.5-6.5 LAST, after all nutrients added</li>
          <li>Let solution circulate 15 minutes before use</li>
        </ul>
      </div>
    `;
    
    showResult('hydro', resultHTML);
  }
  
  function clearHydro() {
    container.querySelector('#reservoirSize').value = '';
    container.querySelector('#growthStage').selectedIndex = 2;
    hideError('hydro');
    hideResult('hydro');
  }
  
  /**
   * Calculate soil application
   */
  function calculateSoil() {
    hideError('soil');
    hideResult('soil');
    
    const count = parseFloat(container.querySelector('#plantCount').value);
    const size = parseFloat(container.querySelector('#containerSize').value);
    
    if (!count || count <= 0) {
      showError('soil', 'Please enter number of plants');
      return;
    }
    
    // Estimate water needed: ~10% of container size per watering
    const waterPerPlant = size * 0.1;
    const totalWater = count * waterPerPlant;
    const rate = 7.5; // ml/gal for soil
    const totalMl = totalWater * rate;
    const totalOz = totalMl * 0.033814;
    
    let resultHTML = `
      <h4>Results for ${count} Plant${count > 1 ? 's' : ''} in ${size}-Gallon Containers</h4>
      <div><strong>${product.name} needed:</strong> ${nf(totalMl)} ml (${nf(totalOz)} oz)</div>
      <div><strong>Mix with water:</strong> ${nf(totalWater)} gallons</div>
      <div><strong>Application rate:</strong> ${rate} ml per gallon</div>
      <div class="calc-tip">
        💡 <strong>Tip:</strong> ${product.tips.soil}
        <ul style="margin:5px 0 0 20px;font-size:.85rem">
          <li>Apply to moist soil for best results</li>
          <li>Can be used as foliar spray (5 ml/gal)</li>
          <li>Repeat every 1-2 weeks as needed</li>
          <li>Compatible with most soil amendments</li>
          <li>Monitor runoff EC to prevent salt buildup</li>
        </ul>
      </div>
    `;
    
    showResult('soil', resultHTML);
  }
  
  function clearSoil() {
    container.querySelector('#plantCount').value = '1';
    container.querySelector('#containerSize').selectedIndex = 1;
    hideError('soil');
    hideResult('soil');
  }
  
  /**
   * Calculate complete 4-part system recipe
   */
  function calculateSystem() {
    hideError('system');
    hideResult('system');
    
    const reservoir = parseFloat(container.querySelector('#systemReservoir').value);
    const stage = container.querySelector('#systemStage').value;
    
    if (!reservoir || reservoir <= 0) {
      showError('system', 'Please enter a valid reservoir size');
      return;
    }
    
    // Complete 4-part recipes (unified feeding schedule)
    const recipes = {
      seedling: { cal: 2.5, grow: 1.25, micro: 1.25, bloom: 1.25, ec: '0.5-0.8' },
      earlyVeg: { cal: 5, grow: 5, micro: 5, bloom: 5, ec: '1.0-1.4' },
      lateVeg: { cal: 7.5, grow: 15, micro: 10, bloom: 5, ec: '1.4-1.8' },
      transition: { cal: 10, grow: 10, micro: 10, bloom: 10, ec: '1.6-2.0' },
      earlyFlower: { cal: 10, grow: 5, micro: 10, bloom: 15, ec: '1.8-2.2' },
      peakFlower: { cal: 10, grow: 0, micro: 10, bloom: 20, ec: '2.0-2.5' },
      lateFlower: { cal: 5, grow: 0, micro: 7.5, bloom: 15, ec: '1.5-2.0' }
    };
    
    const recipe = recipes[stage];
    const calTotal = reservoir * recipe.cal;
    const growTotal = reservoir * recipe.grow;
    const microTotal = reservoir * recipe.micro;
    const bloomTotal = reservoir * recipe.bloom;
    
    // Stage display names
    const stageNames = {
      seedling: 'Seedlings/Clones',
      earlyVeg: 'Early Vegetative',
      lateVeg: 'Late Vegetative',
      transition: 'Transition',
      earlyFlower: 'Early Bloom',
      peakFlower: 'Peak Bloom',
      lateFlower: 'Late Bloom/Flush'
    };
    
    let resultHTML = `
      <h4>Complete 4-Part Recipe for ${nf(reservoir)} Gallons</h4>
      <div><strong>Stage:</strong> ${stageNames[stage]}</div>
      <div><strong>Target EC:</strong> ${recipe.ec}</div>
      <div class="calc-tip">
        <strong>Mix in this order (NEVER mix concentrates directly):</strong>
        <ol style="margin:5px 0 0 20px;font-size:.95rem">
          <li><strong>Cal-Mag Plus:</strong> ${nf(calTotal)} ml (${nf(calTotal * 0.033814)} oz)</li>
          <li><strong>Micro Green:</strong> ${nf(microTotal)} ml (${nf(microTotal * 0.033814)} oz)</li>
          ${growTotal > 0 ? `<li><strong>Grow Green:</strong> ${nf(growTotal)} ml (${nf(growTotal * 0.033814)} oz)</li>` : ''}
          <li><strong>Blossom Green:</strong> ${nf(bloomTotal)} ml (${nf(bloomTotal * 0.033814)} oz)</li>
        </ol>
      </div>
      <div style="margin-top:10px"><strong>Per gallon rates:</strong> Cal-Mag ${recipe.cal}ml, Micro ${recipe.micro}ml${growTotal > 0 ? ', Grow ' + recipe.grow + 'ml' : ''}, Bloom ${recipe.bloom}ml</div>
      <div class="calc-tip">
        💡 <strong>Tip:</strong> ${product.tips.system}
        <ul style="margin:5px 0 0 20px;font-size:.85rem">
          <li>Start with RO or filtered water</li>
          <li>Add each nutrient in the order shown above</li>
          <li>Stir thoroughly between additions (30-60 seconds)</li>
          <li>Adjust pH to 5.5-6.5 LAST (5.8-6.5 during bloom)</li>
          <li>Let circulate 15 minutes before measuring final EC/pH</li>
          <li>Change reservoir weekly for best results</li>
        </ul>
      </div>
    `;
    
    showResult('system', resultHTML);
  }
  
  function clearSystem() {
    container.querySelector('#systemReservoir').value = '';
    container.querySelector('#systemStage').selectedIndex = 2;
    hideError('system');
    hideResult('system');
  }
  
}; // End renderHydroponicsCalculator